import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, MessageCircle, Zap, Users, Wrench, ShieldCheck, Target, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcPlaybooks, bcPlaybookGet } from "@/lib/api";

export function BusinessCenterPlaybooks() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    document.title = "Industry Playbooks | WebsiteTech.pro";
    bcPlaybooks().then((d) => setItems(d.items || []));
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-playbooks">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Industry Playbooks</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">Step-by-step playbooks that map real business problems to specific AI opportunities. Every playbook lists recommended modules, sample AI conversations, automations, KPIs and common mistakes.</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => (
              <Link key={p.slug} to={`/business-center/playbooks/${p.slug}`} data-testid={`bc-playbook-${p.slug}`}
                    className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-indigo-400 hover:shadow-md transition">
                <BookOpen className="w-6 h-6 text-indigo-600" />
                <div className="mt-4 font-bold text-slate-900">{p.industry}</div>
                <div className="mt-1 text-sm text-slate-600">{p.hero}</div>
                <div className="mt-3 text-xs text-slate-500">{p.audience}</div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(p.recommended_modules || []).slice(0, 3).map((m) => (
                    <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{m.split(" ")[0]}</span>
                  ))}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 group-hover:gap-2 transition-all">Open playbook <ArrowRight className="w-3.5 h-3.5" /></div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function BusinessCenterPlaybookDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { bcPlaybookGet(slug).then(setP).catch(() => {}); }, [slug]);
  if (!p) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="pt-32 text-center text-slate-500">Loading…</div></div>;
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-playbook-detail">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center/playbooks" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Playbooks</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{p.industry}</h1>
          <p className="mt-3 text-lg text-slate-700 italic">{p.hero}</p>
          <p className="mt-2 text-slate-600">{p.audience}</p>
          <div className="mt-4 inline-block text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{p.disclosure?.label}</div>

          <Sect icon={AlertTriangle} title="Common problems" tone="rose"><Bullets items={p.common_problems} /></Sect>
          <Sect icon={Users} title="Customer journey"><OrderedList items={p.customer_journey} /></Sect>
          <Sect icon={Target} title="AI opportunities" tone="indigo"><Bullets items={p.ai_opportunities} /></Sect>
          <Sect icon={Zap} title="Recommended WebsiteTech modules" tone="indigo">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {(p.recommended_modules || []).map((m) => <li key={m} className="text-sm bg-indigo-50 border border-indigo-200 text-indigo-900 rounded-lg px-3 py-2 font-semibold">{m}</li>)}
            </ul>
          </Sect>
          <Sect icon={Wrench} title="Recommended integrations"><Bullets items={p.recommended_integrations} /></Sect>

          <Sect icon={MessageCircle} title="Sample AI conversations" tone="emerald">
            <div className="space-y-3">
              {(p.sample_conversations || []).map((c, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`bc-playbook-conv-${i}`}>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Visitor</div>
                  <div className="text-sm text-slate-900">{c.visitor}</div>
                  <div className="mt-3 text-xs uppercase tracking-widest text-indigo-700 font-bold mb-1">Nova (AI Employee)</div>
                  <div className="text-sm text-indigo-950 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">{c.nova}</div>
                </div>
              ))}
            </div>
          </Sect>

          <Sect icon={Zap} title="Sample automations">
            <div className="space-y-2">
              {(p.sample_automations || []).map((a, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`bc-playbook-auto-${i}`}>
                  <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">Trigger</div>
                  <div className="text-sm font-semibold text-slate-900">{a.trigger}</div>
                  <div className="mt-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">Actions</div>
                  <ul className="text-sm text-slate-700 list-decimal list-inside">{(a.actions || []).map((x, j) => <li key={j}>{x}</li>)}</ul>
                </div>
              ))}
            </div>
          </Sect>

          <Sect icon={Wrench} title="Setup requirements"><Bullets items={p.setup_requirements} /></Sect>
          <Sect icon={Target} title="Implementation sequence">
            <div className="space-y-2">
              {(p.implementation_sequence || []).map((w, i) => (
                <div key={i} className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-4">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold">Week {w.week}</div>
                  <div className="text-sm text-slate-800">{w.focus}</div>
                </div>
              ))}
            </div>
          </Sect>
          <Sect icon={ShieldCheck} title="Compliance considerations" tone="emerald"><Bullets items={p.compliance} /></Sect>
          <Sect icon={Target} title="KPIs"><Bullets items={p.kpis} /></Sect>
          <Sect icon={AlertTriangle} title="Common mistakes" tone="rose"><Bullets items={p.common_mistakes} /></Sect>

          <div className="mt-10 rounded-3xl bg-slate-950 text-white p-8">
            <h2 className="text-xl md:text-2xl font-bold">Want this built for your business?</h2>
            <p className="mt-2 text-slate-300 text-sm">We'll turn this playbook into a proposal tailored to your specific business, integrations and budget.</p>
            <Link to="/contact" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-5 py-3 font-semibold transition">Request implementation <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Sect({ icon: Icon, title, tone, children }) {
  const tones = { indigo: "text-indigo-700", rose: "text-rose-700", emerald: "text-emerald-700" };
  return (
    <section className="mt-10">
      <div className={`text-sm font-bold uppercase tracking-widest flex items-center gap-2 ${tones[tone] || "text-slate-700"}`}>
        <Icon className="w-4 h-4" /> {title}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}
function Bullets({ items }) {
  return (
    <ul className="space-y-1.5">
      {(items || []).map((it, i) => (<li key={i} className="text-sm text-slate-800 flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />{it}</li>))}
    </ul>
  );
}
function OrderedList({ items }) {
  return (
    <ol className="space-y-1.5">
      {(items || []).map((it, i) => (<li key={i} className="text-sm text-slate-800 flex items-start gap-2"><span className="inline-flex w-5 h-5 rounded-full bg-indigo-100 text-indigo-800 items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</span>{it}</li>))}
    </ol>
  );
}
